import { SupportedLanguage } from '../localized-defaults';
import { InboundIntent } from './types';

export type ReplyTemplateKey =
  | InboundIntent
  | 'confirm_unknown_time'
  | 'confirm_forwarded'
  | 'confirm_forwarded_manual'
  | 'confirm_conflict_cancelled'
  | 'cancel_unknown_time'
  | 'cancel_forwarded'
  | 'cancel_forwarded_manual'
  | 'reschedule_no_booking'
  | 'reschedule_forwarded_manual'
  | 'late_cancel_blocked'
  | 'late_cancel_blocked_unknown_time'
  | 'late_cancel_link'
  | 'late_cancel_link_unknown_time'
  | 'safe_default';

export type ReplyTemplateMap = Record<
  SupportedLanguage,
  Record<ReplyTemplateKey, string>
>;

const EN_TEMPLATES: Record<ReplyTemplateKey, string> = {
  confirm:
    'Thanks! Your appointment with {businessName} on {date} at {time} is confirmed.',
  confirm_unknown_time:
    'Thanks! Your appointment with {businessName} is confirmed.',
  confirm_forwarded:
    'Thanks! We forwarded your confirmation to {businessName}. They will follow up if needed.',
  confirm_forwarded_manual:
    'Thanks! We forwarded your confirmation to {businessName}.',
  confirm_conflict_cancelled:
    'This appointment with {businessName} was already cancelled, so we could not confirm it. We let {businessName} know.{bookingLink}',
  cancel:
    'Your cancellation has been noted for your appointment with {businessName} on {date} at {time}.{bookingLink}',
  cancel_unknown_time:
    'Your cancellation has been noted for your appointment with {businessName}.{bookingLink}',
  cancel_forwarded:
    'Thanks! We passed your cancellation request to {businessName}. They will follow up if needed.',
  cancel_forwarded_manual:
    'Your cancellation request has been forwarded to {businessName}, however you should contact them directly to confirm.',
  reschedule: 'To reschedule with {businessName}, use this link: {bookingLink}',
  reschedule_no_booking:
    'We forwarded your reschedule request to {businessName}. Please contact them directly to pick a new time.',
  reschedule_forwarded_manual:
    'Your reschedule request has been forwarded to {businessName}, however you should contact them directly to pick a new appointment.',
  question:
    'Thanks for your message. We forwarded it to {businessName} and they will get back to you soon.',
  free_form:
    'Thanks for your message. We forwarded it to {businessName} and they will get back to you soon.',
  noise:
    'Thanks for your reply. If you need help with your appointment, please contact {businessName} directly.',
  late_cancel_blocked:
    'Your appointment can no longer be cancelled by text because the cancellation deadline was {cutoff}. We passed your message to {businessName}. Please contact them directly to cancel. Their cancellation policy: {policyText}',
  late_cancel_blocked_unknown_time:
    'Your appointment can no longer be cancelled by text because the cancellation deadline has passed. We passed your message to {businessName}. Please contact them directly to cancel. Their cancellation policy: {policyText}',
  late_cancel_link:
    'The cancellation deadline for your appointment was {cutoff}, so cancelling now counts as a late cancellation. We passed your message to {businessName}. To cancel anyway, use this link: {link} Their cancellation policy: {policyText}',
  late_cancel_link_unknown_time:
    'The cancellation deadline for your appointment has passed, so cancelling now counts as a late cancellation. We passed your message to {businessName}. To cancel anyway, use this link: {link} Their cancellation policy: {policyText}',
  safe_default:
    'Thanks for your reply. If you need help with your appointment, please contact {businessName} directly.',
};

const ES_TEMPLATES: Record<ReplyTemplateKey, string> = {
  confirm:
    '¡Gracias! Su cita con {businessName} el {date} a las {time} está confirmada.',
  confirm_unknown_time: '¡Gracias! Su cita con {businessName} está confirmada.',
  confirm_forwarded:
    '¡Gracias! Reenviamos su confirmación a {businessName}. Se pondrán en contacto si es necesario.',
  confirm_forwarded_manual:
    '¡Gracias! Reenviamos su confirmación a {businessName}.',
  confirm_conflict_cancelled:
    'Esta cita con {businessName} ya estaba cancelada, por lo que no pudimos confirmarla. Avisamos a {businessName}.{bookingLink}',
  cancel:
    'Su cancelación quedó registrada para su cita con {businessName} el {date} a las {time}.{bookingLink}',
  cancel_unknown_time:
    'Su cancelación quedó registrada para su cita con {businessName}.{bookingLink}',
  cancel_forwarded:
    '¡Gracias! Enviamos su solicitud de cancelación a {businessName}. Se pondrán en contacto si es necesario.',
  cancel_forwarded_manual:
    'Su solicitud de cancelación fue enviada a {businessName}, pero debe contactarlos directamente para confirmarla.',
  reschedule:
    'Para reprogramar con {businessName}, use este enlace: {bookingLink}',
  reschedule_no_booking:
    'Reenviamos su solicitud de reprogramación a {businessName}. Contáctelos directamente para elegir una nueva hora.',
  reschedule_forwarded_manual:
    'Su solicitud de reprogramación fue enviada a {businessName}, pero debe contactarlos directamente para elegir una nueva cita.',
  question:
    'Gracias por su mensaje. Lo reenviamos a {businessName} y le responderán pronto.',
  free_form:
    'Gracias por su mensaje. Lo reenviamos a {businessName} y le responderán pronto.',
  noise:
    'Gracias por su respuesta. Si necesita ayuda con su cita, contacte directamente a {businessName}.',
  late_cancel_blocked:
    'Ya no es posible cancelar su cita por mensaje porque el plazo para cancelar venció el {cutoff}. Pasamos su mensaje a {businessName}. Contáctelos directamente para cancelar. Política de cancelación: {policyText}',
  late_cancel_blocked_unknown_time:
    'Ya no es posible cancelar su cita por mensaje porque el plazo para cancelar ya venció. Pasamos su mensaje a {businessName}. Contáctelos directamente para cancelar. Política de cancelación: {policyText}',
  late_cancel_link:
    'El plazo para cancelar su cita venció el {cutoff}, así que cancelar ahora cuenta como cancelación tardía. Pasamos su mensaje a {businessName}. Si aun así desea cancelar, use este enlace: {link} Política de cancelación: {policyText}',
  late_cancel_link_unknown_time:
    'El plazo para cancelar su cita ya venció, así que cancelar ahora cuenta como cancelación tardía. Pasamos su mensaje a {businessName}. Si aun así desea cancelar, use este enlace: {link} Política de cancelación: {policyText}',
  safe_default:
    'Gracias por su respuesta. Si necesita ayuda con su cita, contacte directamente a {businessName}.',
};

const PT_BR_TEMPLATES: Record<ReplyTemplateKey, string> = {
  confirm:
    'Obrigado! Sua consulta com {businessName} em {date} às {time} está confirmada.',
  confirm_unknown_time:
    'Obrigado! Sua consulta com {businessName} está confirmada.',
  confirm_forwarded:
    'Obrigado! Encaminhamos sua confirmação para {businessName}. Eles entrarão em contato se necessário.',
  confirm_forwarded_manual:
    'Obrigado! Encaminhamos sua confirmação para {businessName}.',
  confirm_conflict_cancelled:
    'Esta consulta com {businessName} já estava cancelada, então não pudemos confirmá-la. Avisamos {businessName}.{bookingLink}',
  cancel:
    'Seu cancelamento foi registrado para a consulta com {businessName} em {date} às {time}.{bookingLink}',
  cancel_unknown_time:
    'Seu cancelamento foi registrado para a consulta com {businessName}.{bookingLink}',
  cancel_forwarded:
    'Obrigado! Encaminhamos seu pedido de cancelamento para {businessName}. Eles entrarão em contato se necessário.',
  cancel_forwarded_manual:
    'Seu pedido de cancelamento foi encaminhado para {businessName}, mas você deve entrar em contato diretamente para confirmar.',
  reschedule: 'Para reagendar com {businessName}, use este link: {bookingLink}',
  reschedule_no_booking:
    'Encaminhamos seu pedido de reagendamento para {businessName}. Entre em contato diretamente para escolher um novo horário.',
  reschedule_forwarded_manual:
    'Seu pedido de reagendamento foi encaminhado para {businessName}, mas você deve entrar em contato diretamente para escolher um novo horário.',
  question:
    'Obrigado pela mensagem. Encaminhamos para {businessName} e eles responderão em breve.',
  free_form:
    'Obrigado pela mensagem. Encaminhamos para {businessName} e eles responderão em breve.',
  noise:
    'Obrigado pela resposta. Se precisar de ajuda com sua consulta, entre em contato com {businessName}.',
  late_cancel_blocked:
    'Não é mais possível cancelar sua consulta por mensagem porque o prazo de cancelamento terminou em {cutoff}. Encaminhamos sua mensagem para {businessName}. Entre em contato diretamente com eles para cancelar. Política de cancelamento: {policyText}',
  late_cancel_blocked_unknown_time:
    'Não é mais possível cancelar sua consulta por mensagem porque o prazo de cancelamento já terminou. Encaminhamos sua mensagem para {businessName}. Entre em contato diretamente com eles para cancelar. Política de cancelamento: {policyText}',
  late_cancel_link:
    'O prazo de cancelamento da sua consulta terminou em {cutoff}, então cancelar agora conta como cancelamento tardio. Encaminhamos sua mensagem para {businessName}. Para cancelar mesmo assim, use este link: {link} Política de cancelamento: {policyText}',
  late_cancel_link_unknown_time:
    'O prazo de cancelamento da sua consulta já terminou, então cancelar agora conta como cancelamento tardio. Encaminhamos sua mensagem para {businessName}. Para cancelar mesmo assim, use este link: {link} Política de cancelamento: {policyText}',
  safe_default:
    'Obrigado pela resposta. Se precisar de ajuda com sua consulta, entre em contato com {businessName}.',
};

const DE_TEMPLATES: Record<ReplyTemplateKey, string> = {
  confirm:
    'Danke! Ihr Termin bei {businessName} am {date} um {time} ist bestätigt.',
  confirm_unknown_time: 'Danke! Ihr Termin bei {businessName} ist bestätigt.',
  confirm_forwarded:
    'Danke! Wir haben Ihre Bestätigung an {businessName} weitergeleitet.',
  confirm_forwarded_manual:
    'Danke! Wir haben Ihre Bestätigung an {businessName} weitergeleitet.',
  confirm_conflict_cancelled:
    'Dieser Termin bei {businessName} war bereits abgesagt, daher konnten wir ihn nicht bestätigen. Wir haben {businessName} informiert.{bookingLink}',
  cancel:
    'Ihre Absage für den Termin bei {businessName} am {date} um {time} wurde notiert.{bookingLink}',
  cancel_unknown_time:
    'Ihre Absage für den Termin bei {businessName} wurde notiert.{bookingLink}',
  cancel_forwarded:
    'Danke! Wir haben Ihre Absage an {businessName} weitergeleitet. Sie melden sich bei Bedarf.',
  cancel_forwarded_manual:
    'Ihre Absageanfrage wurde an {businessName} weitergeleitet. Bitte kontaktieren Sie sie jedoch direkt, um die Absage zu bestätigen.',
  reschedule:
    'Um bei {businessName} umzubuchen, nutzen Sie diesen Link: {bookingLink}',
  reschedule_no_booking:
    'Wir haben Ihre Umbuchungsanfrage an {businessName} weitergeleitet. Bitte kontaktieren Sie sie direkt.',
  reschedule_forwarded_manual:
    'Ihre Umbuchungsanfrage wurde an {businessName} weitergeleitet. Bitte kontaktieren Sie sie jedoch direkt, um einen neuen Termin zu vereinbaren.',
  question:
    'Danke für Ihre Nachricht. Wir haben sie an {businessName} weitergeleitet.',
  free_form:
    'Danke für Ihre Nachricht. Wir haben sie an {businessName} weitergeleitet.',
  noise:
    'Danke für Ihre Antwort. Bei Fragen zum Termin wenden Sie sich bitte an {businessName}.',
  late_cancel_blocked:
    'Ihr Termin kann nicht mehr per SMS abgesagt werden, da die Absagefrist am {cutoff} abgelaufen ist. Wir haben Ihre Nachricht an {businessName} weitergeleitet. Bitte kontaktieren Sie sie direkt, um abzusagen. Stornierungsbedingungen: {policyText}',
  late_cancel_blocked_unknown_time:
    'Ihr Termin kann nicht mehr per SMS abgesagt werden, da die Absagefrist abgelaufen ist. Wir haben Ihre Nachricht an {businessName} weitergeleitet. Bitte kontaktieren Sie sie direkt, um abzusagen. Stornierungsbedingungen: {policyText}',
  late_cancel_link:
    'Die Absagefrist für Ihren Termin ist am {cutoff} abgelaufen, eine Absage gilt jetzt daher als kurzfristige Absage. Wir haben Ihre Nachricht an {businessName} weitergeleitet. Wenn Sie trotzdem absagen möchten, nutzen Sie diesen Link: {link} Stornierungsbedingungen: {policyText}',
  late_cancel_link_unknown_time:
    'Die Absagefrist für Ihren Termin ist abgelaufen, eine Absage gilt jetzt daher als kurzfristige Absage. Wir haben Ihre Nachricht an {businessName} weitergeleitet. Wenn Sie trotzdem absagen möchten, nutzen Sie diesen Link: {link} Stornierungsbedingungen: {policyText}',
  safe_default:
    'Danke für Ihre Antwort. Bei Fragen zum Termin wenden Sie sich bitte an {businessName}.',
};

const FR_TEMPLATES: Record<ReplyTemplateKey, string> = {
  confirm:
    'Merci ! Votre rendez-vous avec {businessName} le {date} à {time} est confirmé.',
  confirm_unknown_time:
    'Merci ! Votre rendez-vous avec {businessName} est confirmé.',
  confirm_forwarded:
    'Merci ! Nous avons transmis votre confirmation à {businessName}.',
  confirm_forwarded_manual:
    'Merci ! Nous avons transmis votre confirmation à {businessName}.',
  confirm_conflict_cancelled:
    'Ce rendez-vous avec {businessName} était déjà annulé, nous n’avons donc pas pu le confirmer. Nous avons informé {businessName}.{bookingLink}',
  cancel:
    'Votre annulation a été enregistrée pour le rendez-vous avec {businessName} le {date} à {time}.{bookingLink}',
  cancel_unknown_time:
    'Votre annulation a été enregistrée pour le rendez-vous avec {businessName}.{bookingLink}',
  cancel_forwarded:
    'Merci ! Nous avons transmis votre demande d’annulation à {businessName}. Ils vous recontacteront si besoin.',
  cancel_forwarded_manual:
    'Votre demande pour annuler a été transmise à {businessName}, mais vous devez les contacter directement pour confirmer.',
  reschedule:
    'Pour reprogrammer avec {businessName}, utilisez ce lien : {bookingLink}',
  reschedule_no_booking:
    'Nous avons transmis votre demande de reprogrammation à {businessName}. Contactez-les directement.',
  reschedule_forwarded_manual:
    'Votre demande de reprogrammation a été transmise à {businessName}, mais vous devez les contacter directement pour choisir un nouveau rendez-vous.',
  question: 'Merci pour votre message. Nous l’avons transmis à {businessName}.',
  free_form:
    'Merci pour votre message. Nous l’avons transmis à {businessName}.',
  noise:
    'Merci pour votre réponse. Pour toute aide, contactez {businessName} directement.',
  late_cancel_blocked:
    'Votre rendez-vous ne peut plus être annulé par SMS car le délai pour annuler a expiré le {cutoff}. Nous avons transmis votre message à {businessName}. Merci de les contacter directement pour annuler. Politique d’annulation : {policyText}',
  late_cancel_blocked_unknown_time:
    'Votre rendez-vous ne peut plus être annulé par SMS car le délai pour annuler a expiré. Nous avons transmis votre message à {businessName}. Merci de les contacter directement pour annuler. Politique d’annulation : {policyText}',
  late_cancel_link:
    'Le délai pour annuler votre rendez-vous a expiré le {cutoff}, toute annulation est donc désormais considérée comme tardive. Nous avons transmis votre message à {businessName}. Pour annuler quand même, utilisez ce lien : {link} Politique d’annulation : {policyText}',
  late_cancel_link_unknown_time:
    'Le délai pour annuler votre rendez-vous a expiré, toute annulation est donc désormais considérée comme tardive. Nous avons transmis votre message à {businessName}. Pour annuler quand même, utilisez ce lien : {link} Politique d’annulation : {policyText}',
  safe_default:
    'Merci pour votre réponse. Pour toute aide, contactez {businessName} directement.',
};

const IT_TEMPLATES: Record<ReplyTemplateKey, string> = {
  confirm:
    'Grazie! Il tuo appuntamento con {businessName} il {date} alle {time} è confermato.',
  confirm_unknown_time:
    'Grazie! Il tuo appuntamento con {businessName} è confermato.',
  confirm_forwarded:
    'Grazie! Abbiamo inoltrato la tua conferma a {businessName}. Ti ricontatteranno se necessario.',
  confirm_forwarded_manual:
    'Grazie! Abbiamo inoltrato la tua conferma a {businessName}.',
  confirm_conflict_cancelled:
    'Questo appuntamento con {businessName} era già cancellato, quindi non abbiamo potuto confermarlo. Abbiamo avvisato {businessName}.{bookingLink}',
  cancel:
    'La tua cancellazione è stata registrata per l’appuntamento con {businessName} il {date} alle {time}.{bookingLink}',
  cancel_unknown_time:
    'La tua cancellazione è stata registrata per l’appuntamento con {businessName}.{bookingLink}',
  cancel_forwarded:
    'Grazie! Abbiamo inoltrato la tua richiesta di cancellazione a {businessName}. Ti ricontatteranno se necessario.',
  cancel_forwarded_manual:
    'La tua richiesta di cancellazione è stata inoltrata a {businessName}, ma dovresti contattarli direttamente per confermarla.',
  reschedule:
    'Per riprogrammare con {businessName}, usa questo link: {bookingLink}',
  reschedule_no_booking:
    'Abbiamo inoltrato la tua richiesta di riprogrammazione a {businessName}. Contattali direttamente per scegliere un nuovo orario.',
  reschedule_forwarded_manual:
    'La tua richiesta di riprogrammazione è stata inoltrata a {businessName}, ma dovresti contattarli direttamente per scegliere un nuovo appuntamento.',
  question:
    'Grazie per il messaggio. Lo abbiamo inoltrato a {businessName} e ti risponderanno presto.',
  free_form:
    'Grazie per il messaggio. Lo abbiamo inoltrato a {businessName} e ti risponderanno presto.',
  noise:
    'Grazie per la risposta. Se hai bisogno di aiuto con l’appuntamento, contatta direttamente {businessName}.',
  late_cancel_blocked:
    'Non è più possibile cancellare il tuo appuntamento via SMS perché il termine per la cancellazione è scaduto il {cutoff}. Abbiamo inoltrato il tuo messaggio a {businessName}. Contattali direttamente per cancellare. Politica di cancellazione: {policyText}',
  late_cancel_blocked_unknown_time:
    'Non è più possibile cancellare il tuo appuntamento via SMS perché il termine per la cancellazione è scaduto. Abbiamo inoltrato il tuo messaggio a {businessName}. Contattali direttamente per cancellare. Politica di cancellazione: {policyText}',
  late_cancel_link:
    'Il termine per cancellare il tuo appuntamento è scaduto il {cutoff}, quindi una cancellazione ora conta come tardiva. Abbiamo inoltrato il tuo messaggio a {businessName}. Per cancellare comunque, usa questo link: {link} Politica di cancellazione: {policyText}',
  late_cancel_link_unknown_time:
    'Il termine per cancellare il tuo appuntamento è scaduto, quindi una cancellazione ora conta come tardiva. Abbiamo inoltrato il tuo messaggio a {businessName}. Per cancellare comunque, usa questo link: {link} Politica di cancellazione: {policyText}',
  safe_default:
    'Grazie per la risposta. Se hai bisogno di aiuto con l’appuntamento, contatta direttamente {businessName}.',
};

export const REPLY_TEMPLATES: ReplyTemplateMap = {
  en: EN_TEMPLATES,
  es: ES_TEMPLATES,
  'pt-BR': PT_BR_TEMPLATES,
  de: DE_TEMPLATES,
  fr: FR_TEMPLATES,
  it: IT_TEMPLATES,
};

/**
 * Appended through the `{bookingLink}` slot. `{url}` is substituted first, so a
 * nested placeholder never reaches the client.
 */
export const BOOKING_LINK_SUFFIX_TEMPLATES: Record<SupportedLanguage, string> =
  {
    en: ' Book again: {url}',
    es: ' Reserve de nuevo: {url}',
    'pt-BR': ' Agende novamente: {url}',
    de: ' Erneut buchen: {url}',
    fr: ' Réserver à nouveau : {url}',
    it: ' Prenota di nuovo: {url}',
  };

/**
 * Swap-ins for an unknown start or timezone: a substituted default zone can name
 * a different day, so these sentences drop the when-clause entirely. A noun
 * phrase in the `{date}` slot cannot work: the templates carry the preposition
 * and article around the slot, so only a sentence written without them stays
 * grammatical outside English.
 */
export const UNKNOWN_TIME_TEMPLATE_KEYS: Partial<
  Record<ReplyTemplateKey, ReplyTemplateKey>
> = {
  confirm: 'confirm_unknown_time',
  cancel: 'cancel_unknown_time',
  late_cancel_blocked: 'late_cancel_blocked_unknown_time',
  late_cancel_link: 'late_cancel_link_unknown_time',
};

/**
 * Adaptive clarification-menu combinations keyed by the applicable options.
 * `cxr`: confirm + cancel + reschedule (confirmation code AND booking link).
 * `cx`: confirm + cancel (confirmation code only).
 * `r`: reschedule only (booking link only).
 * F (forward) is always implicit and appended in every menu. There is no
 * single-letter menu: when only F would remain the menu is not sent at all
 * (see menu-composer `send: false`).
 *
 * Copy is deliberately free of carrier-reserved opt-out keywords
 * (CANCEL / STOP / END / QUIT / UNSUBSCRIBE / REVOKE / OPTOUT) and kept short.
 */
export type MenuTemplateKey = 'cxr' | 'cx' | 'r';

export type MenuTemplateMap = Record<
  SupportedLanguage,
  Record<MenuTemplateKey, string>
>;

const EN_MENU_TEMPLATES: Record<MenuTemplateKey, string> = {
  cxr: 'Reply C to confirm, X to call off, R to reschedule, or F to forward your message to {businessName}.',
  cx: 'Reply C to confirm, X to call off, or F to forward your message to {businessName}.',
  r: 'Reply R to reschedule, or F to forward your message to {businessName}.',
};

const ES_MENU_TEMPLATES: Record<MenuTemplateKey, string> = {
  cxr: 'Responda C para confirmar, X para anular, R para reprogramar o F para enviar su mensaje a {businessName}.',
  cx: 'Responda C para confirmar, X para anular o F para enviar su mensaje a {businessName}.',
  r: 'Responda R para reprogramar o F para enviar su mensaje a {businessName}.',
};

const PT_BR_MENU_TEMPLATES: Record<MenuTemplateKey, string> = {
  cxr: 'Responda C para confirmar, X para desmarcar, R para reagendar ou F para encaminhar sua mensagem para {businessName}.',
  cx: 'Responda C para confirmar, X para desmarcar ou F para encaminhar sua mensagem para {businessName}.',
  r: 'Responda R para reagendar ou F para encaminhar sua mensagem para {businessName}.',
};

const DE_MENU_TEMPLATES: Record<MenuTemplateKey, string> = {
  cxr: 'Antworten Sie C zum Bestaetigen, X zum Absagen, R zum Umbuchen oder F, um Ihre Nachricht an {businessName} weiterzuleiten.',
  cx: 'Antworten Sie C zum Bestaetigen, X zum Absagen oder F, um Ihre Nachricht an {businessName} weiterzuleiten.',
  r: 'Antworten Sie R zum Umbuchen oder F, um Ihre Nachricht an {businessName} weiterzuleiten.',
};

const FR_MENU_TEMPLATES: Record<MenuTemplateKey, string> = {
  cxr: 'Repondez C pour confirmer, X pour annuler, R pour reprogrammer ou F pour transmettre votre message a {businessName}.',
  cx: 'Repondez C pour confirmer, X pour annuler ou F pour transmettre votre message a {businessName}.',
  r: 'Repondez R pour reprogrammer ou F pour transmettre votre message a {businessName}.',
};

const IT_MENU_TEMPLATES: Record<MenuTemplateKey, string> = {
  cxr: 'Rispondi C per confermare, X per disdire, R per riprogrammare o F per inoltrare il tuo messaggio a {businessName}.',
  cx: 'Rispondi C per confermare, X per disdire o F per inoltrare il tuo messaggio a {businessName}.',
  r: 'Rispondi R per riprogrammare o F per inoltrare il tuo messaggio a {businessName}.',
};

export const MENU_TEMPLATES: MenuTemplateMap = {
  en: EN_MENU_TEMPLATES,
  es: ES_MENU_TEMPLATES,
  'pt-BR': PT_BR_MENU_TEMPLATES,
  de: DE_MENU_TEMPLATES,
  fr: FR_MENU_TEMPLATES,
  it: IT_MENU_TEMPLATES,
};

/**
 * One-shot clarification copy shown alongside the adaptive menu when an inbound
 * reply is actionable but too ambiguous to auto-act.
 */
export const CLARIFICATION_TEMPLATES: Record<SupportedLanguage, string> = {
  en: 'We were not sure how to help with your appointment with {businessName}.',
  es: 'No estamos seguros de como ayudarle con su cita con {businessName}.',
  'pt-BR':
    'Nao temos certeza de como ajudar com sua consulta com {businessName}.',
  de: 'Wir waren uns nicht sicher, wie wir bei Ihrem Termin bei {businessName} helfen koennen.',
  fr: 'Nous ne savions pas comment vous aider pour votre rendez-vous avec {businessName}.',
  it: 'Non eravamo sicuri di come aiutarti con il tuo appuntamento con {businessName}.',
};

/**
 * One-line context stamp prepended to a business escalation when a client's
 * intent stays unclear after the menu, so the business gets a clean handoff
 * rather than a naked forward.
 */
export const CONTEXT_STAMP_TEMPLATES: Record<SupportedLanguage, string> = {
  en: "Client replied, intent unclear after menu, original: '{originalMessage}'",
  es: "El cliente respondio, intencion poco clara tras el menu, original: '{originalMessage}'",
  'pt-BR':
    "Cliente respondeu, intencao pouco clara apos o menu, original: '{originalMessage}'",
  de: "Kunde hat geantwortet, Absicht nach dem Menue unklar, Original: '{originalMessage}'",
  fr: "Le client a repondu, intention peu claire apres le menu, message d'origine: '{originalMessage}'",
  it: "Il cliente ha risposto, intenzione poco chiara dopo il menu, originale: '{originalMessage}'",
};

/**
 * Context stamp for an escalation raised with NO menu outstanding, where the
 * menu-unclear line above would describe a menu that was never sent.
 */
export const HANDOFF_STAMP_TEMPLATES: Record<SupportedLanguage, string> = {
  en: "Client replied, we could not handle this one, passing it to you, original: '{originalMessage}'",
  es: "El cliente respondio, no pudimos gestionarlo, se lo pasamos a usted, original: '{originalMessage}'",
  'pt-BR':
    "Cliente respondeu, nao conseguimos resolver, estamos repassando para voce, original: '{originalMessage}'",
  de: "Kunde hat geantwortet, wir konnten es nicht selbst erledigen, wir geben es an Sie weiter, Original: '{originalMessage}'",
  fr: "Le client a repondu, nous n'avons pas pu traiter le message, nous vous le transmettons, message d'origine: '{originalMessage}'",
  it: "Il cliente ha risposto, non siamo riusciti a gestirlo, lo passiamo a te, originale: '{originalMessage}'",
};

/** Context stamp for a confirm/cancel the secretary could not put on the appointment. */
export const APPLY_FAILED_STAMP_TEMPLATES: Record<SupportedLanguage, string> = {
  en: "We could not apply the client's request to this appointment, please review it, original: '{originalMessage}'",
  es: "No pudimos aplicar la solicitud del cliente a esta cita, por favor revisela, original: '{originalMessage}'",
  'pt-BR':
    "Nao conseguimos aplicar o pedido do cliente a este compromisso, por favor verifique, original: '{originalMessage}'",
  de: "Wir konnten die Anfrage des Kunden fuer diesen Termin nicht uebernehmen, bitte pruefen Sie ihn, Original: '{originalMessage}'",
  fr: "Nous n'avons pas pu appliquer la demande du client a ce rendez-vous, merci de le verifier, message d'origine: '{originalMessage}'",
  it: "Non siamo riusciti ad applicare la richiesta del cliente a questo appuntamento, ti chiediamo di verificarlo, originale: '{originalMessage}'",
};

/** Context stamp for a request that IS on the appointment but failed to reach Google (S-616). */
export const APPLIED_AFTER_FAILURE_STAMP_TEMPLATES: Record<
  SupportedLanguage,
  string
> = {
  en: "The client's request was applied to this appointment, but your calendar may not show it yet, original: '{originalMessage}'",
  es: "La solicitud del cliente se aplico a esta cita, pero su calendario puede no mostrarlo aun, original: '{originalMessage}'",
  'pt-BR':
    "O pedido do cliente foi aplicado a este compromisso, mas seu calendario pode ainda nao mostrar isso, original: '{originalMessage}'",
  de: "Die Anfrage des Kunden wurde fuer diesen Termin uebernommen, aber Ihr Kalender zeigt es moeglicherweise noch nicht, Original: '{originalMessage}'",
  fr: "La demande du client a ete appliquee a ce rendez-vous, mais votre agenda ne l'affiche peut-etre pas encore, message d'origine: '{originalMessage}'",
  it: "La richiesta del cliente e stata applicata a questo appuntamento, ma il tuo calendario potrebbe non mostrarla ancora, originale: '{originalMessage}'",
};

/** Owner-facing stamp for a client cancel refused by a cancellation policy (S-625). */
export const LATE_CANCEL_POLICY_STAMP_TEMPLATES: Record<
  SupportedLanguage,
  string
> = {
  en: "Client asked to cancel inside your cancellation window; we did not cancel. Original: '{originalMessage}'",
  es: "El cliente pidio cancelar fuera del plazo de su politica de cancelacion; no cancelamos la cita. Original: '{originalMessage}'",
  'pt-BR':
    "O cliente pediu para cancelar fora do prazo da sua politica de cancelamento; nao cancelamos a consulta. Original: '{originalMessage}'",
  de: "Kunde wollte nach Ablauf Ihrer Absagefrist absagen; wir haben den Termin nicht abgesagt. Original: '{originalMessage}'",
  fr: "Le client a demande a annuler hors du delai de votre politique d'annulation; nous n'avons pas annule le rendez-vous. Message d'origine: '{originalMessage}'",
  it: "Il cliente ha chiesto di cancellare oltre il termine della tua politica di cancellazione; non abbiamo cancellato l'appuntamento. Originale: '{originalMessage}'",
};

/** Not the late-cancel copy: whether the cancel fell inside the window is unknown. */
export const CANCEL_POLICY_UNCHECKED_STAMP_TEMPLATES: Record<
  SupportedLanguage,
  string
> = {
  en: "Client asked to cancel; we could not check the cancellation policy for this appointment, so we did not cancel. Original: '{originalMessage}'",
  es: "El cliente pidio cancelar; no pudimos verificar la politica de cancelacion de esta cita, asi que no la cancelamos. Original: '{originalMessage}'",
  'pt-BR':
    "O cliente pediu para cancelar; nao conseguimos verificar a politica de cancelamento desta consulta, entao nao cancelamos. Original: '{originalMessage}'",
  de: "Kunde wollte absagen; wir konnten die Absageregeln fuer diesen Termin nicht pruefen und haben ihn daher nicht abgesagt. Original: '{originalMessage}'",
  fr: "Le client a demande a annuler; nous n'avons pas pu verifier la politique d'annulation de ce rendez-vous, donc nous ne l'avons pas annule. Message d'origine: '{originalMessage}'",
  it: "Il cliente ha chiesto di cancellare; non abbiamo potuto verificare la politica di cancellazione di questo appuntamento, quindi non l'abbiamo cancellato. Originale: '{originalMessage}'",
};

export interface ReplyPlaceholders {
  businessName: string;
  date: string;
  time: string;
  bookingLink: string;
  cutoff?: string;
  link?: string;
  policyText?: string;
}

export function renderReplyTemplate(
  template: string,
  placeholders: ReplyPlaceholders,
): string {
  // Global regexes, not replaceAll: the add-on compiles shared with a pre-ES2021 lib.
  // Owner text goes last through a replacer, so it is never re-scanned or `$`-expanded.
  const { cutoff = '', link = '', policyText = '' } = placeholders;
  return template
    .replace(/\{businessName\}/g, placeholders.businessName)
    .replace(/\{date\}/g, placeholders.date)
    .replace(/\{time\}/g, placeholders.time)
    .replace(/\{bookingLink\}/g, placeholders.bookingLink)
    .replace(/\{cutoff\}/g, () => cutoff)
    .replace(/\{link\}/g, () => link)
    .replace(/\{policyText\}/g, () => policyText);
}
